import type { ProductAd } from '@/scopes/product-ads/domain/types'
import { useOnScreen } from '@/scopes/common/components/hooks/useOnScreen'
import { LazyImage } from '@/scopes/common/components/Image'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createProductAdItemClickedEvent } from '@/scopes/product-ads/domain/analytics'
import { useProductAdsRendered, useUpdateProductAdViewed } from '@/scopes/product-ads/domain/hooks'
import { radius, space, tok } from '@sunwebgroup/ui-components'
import { Typography } from '@sunwebgroup/ui-components/uds'
import { useEffect, useRef, useState } from 'react'
import { styled } from 'styled-components'

interface ProductAdProps {
  data: ProductAd
  componentType: string
}

const Wrapper = styled.div`
  position: relative;
  width: 260px;
  box-shadow: ${tok('primitives_box_shadows_level_2')};
  border-radius: ${radius('primitives_corner_radius_l')};
  background-color: ${tok('semantics_views_background_color')};
`
const ImageWrapper = styled.div`
  height: 147px;
  width: 260px;
  border-radius: ${radius('primitives_corner_radius_l')} ${radius('primitives_corner_radius_l')} 0px 0px;
  overflow: hidden;
`

const Footer = styled.div`
  padding: ${space('primitives_space_16')};
  display: flex;
  flex-direction: column;
`

const SubHeading = styled(Typography)`
  color: ${tok('semantics_content_secondary_color')};
`

const Link = styled.a`
  // Link styles are needed to override the MVC styles
  // TODO: remove once page is fully migrated to React
  text-decoration: none;

  // ------------------------------

  &:hover {
    ${LazyImage} img {
      filter: brightness(0.85);
      transition: all 0.3s ease-in-out;
    }
  }

  &:not(:hover) {
    ${LazyImage} img {
      transition: all 0.3s ease-in-out;
    }
  }
`

export function ProductAdComponent({ data, componentType }: ProductAdProps) {
  const ref = useRef<HTMLDivElement>(null)
  const isOnScreen = useOnScreen(ref, { threshold: 0.5 })
  const [isViewed, setIsViewed] = useState(false)
  const { sendEvent } = useAnalytics()

  // we notify the productAd is rendered
  useProductAdsRendered(data.rendered)

  const { update } = useUpdateProductAdViewed(data.viewed)

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> = setTimeout(() => {}, 0)
    if (isOnScreen && !isViewed) {
      timer = setTimeout(async () => {
        if (isOnScreen) {
          await update()
          setIsViewed(true)
        }
      }, 1000)
    }
    else {
      clearTimeout(timer)
    }

    return () => clearTimeout(timer)
  }, [data.viewed, isOnScreen, isViewed, update])

  const handleOnClick = () => {
    localStorage.setItem('C_URL', data.conversionUrl ?? '')
    localStorage.setItem('acco_ID', data.accoId)
    sendEvent(createProductAdItemClickedEvent({ ads: [data], type: componentType }))
  }

  return (
    <Wrapper ref={ref} className={`FID_${data.flightId}`}>
      <Link href={data.clicked} target="_blank" rel="noreferrer" onClick={handleOnClick}>
        <ImageWrapper>
          <LazyImage
            src={data.image}
            srcSet={[260, 147]}
            alt={data.heading}
          />
        </ImageWrapper>
        <Footer>
          <Typography $variant="display-i-regular">{data.heading}</Typography>
          <SubHeading $variant="body-s-regular-tight">
            {data.subHeading}
          </SubHeading>
        </Footer>
      </Link>
    </Wrapper>
  )
}
